// components/formDialog/TicketDialog.tsx
import InputField from "@/components/form/components/InputField";
import SelectField from "@/components/form/components/SelectField";
import TextareaField from "@/components/form/components/Textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import {
  useCreateTicketMutation,
  useUpdateTicketMutation,
} from "@/hooks/api/ticket.queries";
import {
  TicketCategory,
  TicketCreateType,
  TicketParamsType,
  TicketPriority,
  TicketStatus,
  TicketType,
} from "@/services/ticket.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

type TicketDialogProps = {
  mode: "create" | "edit";
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: TicketParamsType;
  ticketId?: string;
  ticketData?: TicketType | null;
  onCancel: () => void;
};

const STATUS_OPTIONS: { label: string; value: TicketStatus }[] = [
  { label: "Open", value: "open" },
  { label: "In Progress", value: "in_progress" },
  { label: "Resolved", value: "resolved" },
  { label: "Closed", value: "closed" },
];

const PRIORITY_OPTIONS: { label: string; value: TicketPriority }[] = [
  { label: "Critical", value: "critical" },
  { label: "High", value: "high" },
  { label: "Medium", value: "medium" },
  { label: "Low", value: "low" },
];

const CATEGORY_OPTIONS: { label: string; value: TicketCategory }[] = [
  { label: "Internship", value: "Internship" },
  { label: "Placement", value: "Placement" },
  { label: "Training", value: "Training" },
  { label: "Academic", value: "Academic" },
  { label: "Technical", value: "Technical" },
  { label: "General", value: "General" },
];

const ROLE_OPTIONS: { label: string; value: string }[] = [
  { label: "Student", value: "Student" },
  { label: "Staff", value: "Staff" },
];

const ticketSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  status: z.enum(["open", "in_progress", "resolved", "closed"]),
  priority: z.enum(["critical", "high", "medium", "low"]),
  category: z.enum([
    "Internship",
    "Placement",
    "Training",
    "Academic",
    "Technical",
    "General",
  ]),
  raisedByName: z.string().min(1, "Raised By Name is required"),
  raisedByRole: z.string().min(1, "Raised By Role is required"),
  assignedTo: z.string().optional(),
});

type TicketFormType = z.infer<typeof ticketSchema>;

const TicketDialog = ({
  mode,
  isOpen,
  onOpenChange,
  filters,
  ticketId,
  ticketData,
  onCancel,
}: TicketDialogProps) => {
  const createMutation = useCreateTicketMutation(filters);
  const updateMutation = useUpdateTicketMutation(filters);
  const isPending = createMutation.isPending || updateMutation.isPending;

  const form = useForm<TicketFormType>({
    resolver: zodResolver(ticketSchema),
    defaultValues: {
      title: "",
      description: "",
      status: "open",
      priority: "medium",
      category: "General",
      raisedByName: "",
      raisedByRole: "Student",
      assignedTo: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && ticketData) {
        setTimeout(() => {
          form.reset({
            title: ticketData.title || "",
            description: ticketData.description || "",
            status: ticketData.status || "open",
            priority: ticketData.priority || "medium",
            category: ticketData.category || "General",
            raisedByName: ticketData.raisedBy?.name || "",
            raisedByRole: ticketData.raisedBy?.role || "Student",
            assignedTo: ticketData.assignedTo?.name || "",
          });
        }, 0);
      } else {
        form.reset({
          title: "",
          description: "",
          status: "open",
          priority: "medium",
          category: "General",
          raisedByName: "",
          raisedByRole: "Student",
          assignedTo: "",
        });
      }
    }
  }, [mode, ticketData, isOpen, form]);

  const handleSubmit = (values: TicketFormType) => {
    const submissionData: TicketCreateType = {
      title: values.title,
      description: values.description,
      status: values.status,
      priority: values.priority,
      category: values.category,
      raisedBy: { name: values.raisedByName, role: values.raisedByRole },
      assignedTo: {
        name: values.assignedTo || "",
        role: "",
        email: "",
        phone: "",
      },
    };

    if (mode === "create") {
      createMutation.mutate(submissionData, { onSuccess: onCancel });
    } else if (mode === "edit" && ticketId) {
      updateMutation.mutate(
        { _id: ticketId, data: submissionData },
        { onSuccess: onCancel }
      );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-xl"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Raise New Ticket" : "Edit Ticket"}
          </DialogTitle>
          <p className="text-muted-foreground mt-1 text-sm">
            Fill in the details to{" "}
            {mode === "create" ? "raise a new ticket" : "edit the ticket"}
          </p>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="grid grid-cols-1 gap-4 py-2 md:grid-cols-12"
          >
            {/* Title */}
            <div className="md:col-span-12">
              <InputField<TicketFormType>
                type="text"
                name="title"
                label="Title"
                placeholder="Brief summary of the issue"
                required
                disabled={isPending}
              />
            </div>

            {/* Description */}
            <div className="md:col-span-12">
              <TextareaField<TicketFormType>
                name="description"
                label="Description"
                placeholder="Describe the issue in detail..."
                required
                disabled={isPending}
              />
            </div>

            {/* Status + Priority */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:col-span-12">
              <SelectField<TicketFormType>
                name="status"
                label="Status"
                options={STATUS_OPTIONS}
                required
                disabled={isPending}
              />
              <SelectField<TicketFormType>
                name="priority"
                label="Priority"
                options={PRIORITY_OPTIONS}
                required
                disabled={isPending}
              />
            </div>

            {/* Category */}
            <div className="md:col-span-12">
              <SelectField<TicketFormType>
                name="category"
                label="Category"
                options={CATEGORY_OPTIONS}
                required
                disabled={isPending}
              />
            </div>

            {/* Raised By */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:col-span-12">
              <InputField<TicketFormType>
                type="text"
                name="raisedByName"
                label="Raised By (Name)"
                placeholder="Full name"
                required
                disabled={isPending}
              />
              <SelectField<TicketFormType>
                name="raisedByRole"
                label="Role"
                options={ROLE_OPTIONS}
                required
                disabled={isPending}
              />
            </div>

            {/* Assigned To */}
            <div className="md:col-span-12">
              <InputField<TicketFormType>
                type="text"
                name="assignedTo"
                label="Assigned To"
                placeholder="e.g. Prof. John Davis"
                disabled={isPending}
              />
            </div>

            <DialogFooter className="mt-4 md:col-span-12">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? mode === "create"
                    ? "Submitting..."
                    : "Saving..."
                  : mode === "create"
                    ? "Submit Ticket"
                    : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default TicketDialog;
